import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, limit, getDocs,
  startAfter, where, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { PageHeader, Table, Badge, Card, GoldBtn,
  FilterBar, SearchInput, Spinner } from '../components/UI';

const ACCENT = '#008751';
const GOLD = '#D4AF37';
const PAGE_SIZE = 25;

// ── Readable export renderer ────────────────────────────────────────────────
// Turns the raw adminExportUserData JSON (Firestore timestamps as
// {_seconds,_nanoseconds}, machine layout) into a clean, court-ready HTML
// transcript. Pure client-side — no extra backend call, so the audit log
// still records exactly one access.
function fmtTs(v) {
  if (v && typeof v === 'object' && '_seconds' in v) {
    return new Date(v._seconds * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  }
  if (typeof v === 'number') {
    const ms = v > 1e11 ? v : v * 1000;
    try { return new Date(ms).toISOString().replace('T', ' ').slice(0, 19) + ' UTC'; } catch { return String(v); }
  }
  return v ? String(v) : '—';
}
function esc(x) {
  return String(x == null ? '—' : x)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function buildReadableExportHtml(d) {
  const u = d.user || {};
  const convs = d.conversations || [];
  const deleted = d.deletedConversations || [];
  const counts = d.counts || {};
  const order = ['displayName','email','phone','uid','gender','dateOfBirth','ageGroup',
    'country','language','schoolLevel','profession','subscriptionTier','subscriptionExpiry',
    'profileComplete','referralCode','referredBy','parentUID','streak','totalQuestionsAsked',
    'createdAt','lastActive'];
  const tsFields = new Set(['createdAt','lastActive','subscriptionExpiry','dateOfBirth']);
  const renderConv = (c, idx) => {
    let s = `<div class="conv"><h2>Conversation ${esc(idx)}: ${esc(c.title || c.subject || '(untitled)')}</h2>`;
    s += `<div class="time">Subject: ${esc(c.subject)} · Category: ${esc(c.category)} · Started ${esc(fmtTs(c.createdAt))} · ${esc(c.messageCount)} messages · ID ${esc(c.id)}</div>`;
    for (const m of (c.messages || [])) {
      const role = m.role || '?';
      const cls = role === 'user' ? 'user' : 'assistant';
      const who = role === 'user' ? 'User' : role === 'assistant' ? 'Oráku AI' : role[0].toUpperCase() + role.slice(1);
      s += `<div class="msg ${cls}"><span class="role">${esc(who)}</span> <span class="time">${esc(fmtTs(m.createdAt))}</span><div class="content">${esc(m.content)}</div></div>`;
    }
    return s + '</div>';
  };
  let h = `<!doctype html><meta charset="utf-8"><title>Oráku Export — ${esc(u.uid)}</title>
<style>body{font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;max-width:820px;margin:24px auto;padding:0 16px;color:#1a1a1a}
h1{font-size:20px}h2{font-size:16px;border-bottom:2px solid #c9a227;padding-bottom:4px;margin-top:28px}
.meta{background:#faf6e8;border:1px solid #e6d9a8;border-radius:8px;padding:12px 16px;font-size:13px}
.kv{display:grid;grid-template-columns:170px 1fr;gap:2px 12px}.kv div:nth-child(odd){color:#666}
.msg{margin:10px 0;padding:8px 12px;border-radius:10px}.user{background:#eef4ff;border:1px solid #d4e2ff}
.assistant{background:#f4f4f4;border:1px solid #e2e2e2}.role{font-weight:700;font-size:12px;color:#555}
.time{color:#999;font-size:11px}.content{white-space:pre-wrap;margin-top:4px}
.conv{border:1px solid #ddd;border-radius:12px;padding:14px 18px;margin:18px 0}</style>
<h1>Oráku Smart AI — User Data Export</h1>
<div class="meta">Exported by <b>${esc(d.exportedBy)}</b> &nbsp;·&nbsp; ${esc(counts.active)} conversations, ${esc(counts.deleted)} deleted<br>Generated ${esc(new Date().toISOString().replace('T',' ').slice(0,19))} UTC</div>
<h2>Account Holder</h2><div class="meta kv">`;
  for (const k of order) {
    if (k in u) {
      const v = tsFields.has(k) ? fmtTs(u[k]) : u[k];
      h += `<div>${esc(k)}</div><div>${esc(v)}</div>`;
    }
  }
  h += '</div>';
  convs.forEach((c, i) => { h += renderConv(c, i + 1); });
  if (deleted.length) {
    h += '<h2>Deleted Conversations (retained for safety/legal)</h2>';
    deleted.forEach((c, i) => { h += renderConv(c, 'D' + (i + 1)); });
  }
  return h;
}
function downloadBlob(content, type, filename) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const TIER_OPTIONS = [
  {value:'free',label:'Free'},{value:'student',label:'Student'},
  {value:'premium',label:'Oga'},{value:'scholar',label:'Odogwu'},
  {value:'family',label:'Family'},
];
const COUNTRY_OPTIONS = [
  {value:'NG',label:'Nigeria'},{value:'US',label:'United States'},
  {value:'GB',label:'United Kingdom'},{value:'GH',label:'Ghana'},
  {value:'ZA',label:'South Africa'},{value:'CA',label:'Canada'},
];
const PLATFORM_OPTIONS = [
  {value:'ios',label:'iPhone (iOS)'},{value:'android',label:'Android'},{value:'web',label:'Web'},
];
// Derive a user's platform: explicit `platform` field (web/ios/android) wins;
// otherwise fall back to the in-app-purchase platform (apple→iOS, google→Android).
export function platformOf(u) {
  const p = (u.platform || '').toLowerCase();
  if (p === 'ios' || p === 'android' || p === 'web') return p;
  const iap = (u.iapPlatform || '').toLowerCase();
  if (iap === 'apple') return 'ios';
  if (iap === 'google') return 'android';
  return 'unknown';
}
export const PLATFORM_META = {
  ios:     { label:'iPhone',  emoji:'', color:'#0A84FF' },
  android: { label:'Android', emoji:'', color:'#3DDC84' },
  web:     { label:'Web',     emoji:'', color:'#D4AF37' },
  unknown: { label:'Unknown', emoji:'',  color:'#6b7280' },
};

export default function Users() {
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [lastDoc,    setLastDoc]    = useState(null);
  const [hasMore,    setHasMore]    = useState(true);
  const [filters,    setFilters]    = useState({});
  const [search,     setSearch]     = useState('');
  const [selected,   setSelected]   = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadUsers(true); }, [filters]);

  async function loadUsers(reset = false) {
    setLoading(true);
    try {
      let q = query(collection(db,'users'), orderBy('createdAt','desc'), limit(PAGE_SIZE));
      if (filters.tier)    q = query(q, where('subscriptionTier','==',filters.tier));
      if (filters.country) q = query(q, where('country','==',filters.country));
      if (!reset && lastDoc) q = query(q, startAfter(lastDoc));

      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({id:d.id,...d.data()}));
      setUsers(reset ? data : [...users, ...data]);
      setLastDoc(snap.docs[snap.docs.length-1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  const filtered = users.filter(u => {
    // Platform is a derived value, so it's filtered client-side (not in the query).
    if (filters.platform && platformOf(u) !== filters.platform) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.displayName||'').toLowerCase().includes(s) ||
           (u.email||'').toLowerCase().includes(s);
  });

  // ── ACTIONS ──────────────────────────────────────────────────────────────
  async function updateTier(uid, tier) {
    setActionLoading(true);
    try {
      const expiry = new Date(Date.now() + 30*24*60*60*1000);
      await updateDoc(doc(db,'users',uid), {
        subscriptionTier: tier,
        subscriptionExpiry: Timestamp.fromDate(expiry),
      });
      setUsers(prev => prev.map(u => u.id === uid ? {...u,subscriptionTier:tier} : u));
      if (selected?.id === uid) setSelected(prev => ({...prev,subscriptionTier:tier}));
      alert(`Tier updated to ${tier}`);
    } catch(e) { alert('Error: '+e.message); }
    setActionLoading(false);
  }

  async function suspendUser(uid) {
    if (!window.confirm('Suspend this account? They will not be able to use Oraku.')) return;
    setActionLoading(true);
    try {
      await updateDoc(doc(db,'users',uid), { suspended: true });
      setUsers(prev => prev.map(u => u.id === uid ? {...u,suspended:true} : u));
      alert('Account suspended');
    } catch(e) { alert('Error: '+e.message); }
    setActionLoading(false);
  }

  async function unsuspendUser(uid) {
    setActionLoading(true);
    try {
      await updateDoc(doc(db,'users',uid), { suspended: false });
      setUsers(prev => prev.map(u => u.id === uid ? {...u,suspended:false} : u));
      if (selected?.id === uid) setSelected(prev => ({...prev,suspended:false}));
      alert('Account reactivated');
    } catch(e) { alert('Error: '+e.message); }
    setActionLoading(false);
  }

  async function flagUser(uid) {
    setActionLoading(true);
    try {
      await updateDoc(doc(db,'users',uid), { flagged: true });
      alert('Account flagged for review');
    } catch(e) { alert('Error: '+e.message); }
    setActionLoading(false);
  }

  const fmtDate = (ts) => ts?.toDate
    ? ts.toDate().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})
    : '—';

  const fmtDays = (ts) => {
    if (!ts?.toDate) return '—';
    const diff = Date.now() - ts.toDate().getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  };

  const getTierBadgeStyle = (tier) => {
    if (tier === 'scholar' || tier === 'premium') {
      return { border: `1px solid rgba(212,175,55,0.3)` };
    }
    return {};
  };

  const COLUMNS = [
    { key:'displayName', label:'Name',
      render:(v,row) => (
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:34,height:34,borderRadius:'50%',
            background:'linear-gradient(135deg, rgba(0,135,81,0.2), rgba(212,175,55,0.1))',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,
            fontWeight:700,color:ACCENT,flexShrink:0,
            border:'1px solid rgba(0,135,81,0.2)'}}>
            {(v||'?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{fontWeight:600,fontSize:13}}>{v || 'Unknown'}</div>
            <div style={{fontSize:11,color:'rgba(255,255,255,0.3)',marginTop:2}}>{row.email}</div>
          </div>
        </div>
      )},
    { key:'subscriptionTier', label:'Tier',
      render: v => {
        const isGold = v === 'scholar' || v === 'premium';
        return (
          <span style={{padding:'4px 12px',borderRadius:20,fontSize:11,
            fontWeight:600,fontFamily:'DM Mono',letterSpacing:0.5,
            background: isGold ? 'rgba(212,175,55,0.18)' : undefined,
            color: isGold ? GOLD : undefined,
            border: isGold ? `1px solid rgba(212,175,55,0.25)` : undefined,
            ...(!isGold ? {} : {}),
            display:'inline-block'}}>
            {isGold ? (
              <Badge label={v||'free'} color={v||'free'} />
            ) : (
              <Badge label={v||'free'} color={v||'free'} />
            )}
          </span>
        );
      }},
    { key:'platform', label:'Platform',
      render: (v,row) => {
        const p = PLATFORM_META[platformOf(row)];
        return (
          <span style={{padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:600,
            fontFamily:'DM Mono',letterSpacing:0.3,display:'inline-block',
            background:p.color+'22',color:p.color,border:`1px solid ${p.color}44`}}>
            {p.label}
          </span>
        );
      }},
    { key:'totalQuestionsAsked', label:'Messages', render: v => (
      <span style={{fontFamily:'DM Mono',fontSize:13}}>{v || 0}</span>
    )},
    { key:'lastActive', label:'Last Active', render: fmtDays },
    { key:'suspended', label:'Status',
      render: (v,row) => v
        ? <Badge label="Suspended" color="flagged" />
        : row.flagged
          ? <Badge label="Flagged" color="open" />
          : <Badge label="Active" color="active" />
    },
  ];

  return (
    <div style={{padding:32}}>
      <PageHeader title="Users"
        subtitle={`${users.length} users loaded`} />

      {/* Filters */}
      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search name or email..." />
        <FilterBar
          filters={[
            {key:'tier',     label:'Tier',     options:TIER_OPTIONS},
            {key:'platform', label:'Platform', options:PLATFORM_OPTIONS},
            {key:'country',  label:'Country',  options:COUNTRY_OPTIONS},
          ]}
          values={filters}
          onChange={(k,v) => setFilters(prev => ({...prev,[k]:v}))} />
      </div>

      {/* Table */}
      <div style={{background:'#1A2332',border:'1px solid rgba(255,255,255,0.06)',
        borderRadius:16,overflow:'hidden',marginBottom:16}}>
        <Table
          columns={COLUMNS}
          data={filtered}
          loading={loading && users.length === 0}
          onRowClick={setSelected} />
      </div>

      {hasMore && !loading && (
        <div style={{textAlign:'center',marginTop:16}}>
          <GoldBtn onClick={() => loadUsers(false)}>Load More</GoldBtn>
        </div>
      )}
      {loading && users.length > 0 && <Spinner />}

      {/* User Detail Modal */}
      {selected && (
        <UserModal user={selected} onClose={() => setSelected(null)}
          onTierChange={updateTier} onSuspend={suspendUser}
          onUnsuspend={unsuspendUser}
          onFlag={flagUser} loading={actionLoading} fmtDate={fmtDate} />
      )}
    </div>
  );
}

// Per-user economics: tokens, AI spend, revenue, margin. Fetched on open.
function UserEconomics({ uid }) {
  const [d, setD] = useState(null);
  const [state, setState] = useState('loading');
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'adminUserEconomics');
        const res = await fn({ uid });
        if (alive) { setD(res.data); setState('ok'); }
      } catch (e) { if (alive) setState('err'); }
    })();
    return () => { alive = false; };
  }, [uid]);

  const box = { background:'#0F1419', border:'1px solid rgba(255,255,255,0.05)', borderRadius:12, padding:'14px 16px', marginBottom:24 };
  if (state === 'loading') return <div style={{...box, color:'rgba(255,255,255,0.4)', fontSize:13}}>Loading economics…</div>;
  if (state === 'err' || !d) return <div style={{...box, color:'rgba(255,255,255,0.4)', fontSize:13}}>Economics unavailable.</div>;

  const usd = (n) => '$' + (Number(n)||0).toFixed(2);
  const k = (n) => { n = Number(n)||0; return n>=1e6 ? (n/1e6).toFixed(1)+'M' : n>=1e3 ? (n/1e3).toFixed(1)+'K' : String(n); };
  const net = (d.revenue.monthly||0) - (d.spend.month||0);
  const cell = (label, val, color) => (
    <div style={{textAlign:'center'}}>
      <div style={{fontSize:10,color:'rgba(255,255,255,0.3)',textTransform:'uppercase',letterSpacing:1,marginBottom:5}}>{label}</div>
      <div style={{fontSize:16,fontWeight:800,fontFamily:'Syne',color:color||'#fff'}}>{val}</div>
    </div>
  );

  return (
    <div style={box}>
      <div style={{fontSize:11,color:GOLD,fontWeight:800,letterSpacing:1,textTransform:'uppercase',marginBottom:12}}>💰 Economics</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:14}}>
        {cell('Pays / mo', usd(d.revenue.monthly), '#30D158')}
        {cell('AI cost 30d', usd(d.spend.month), '#FF6961')}
        {cell('Net / mo', usd(net), net>=0 ? '#30D158' : '#FF6961')}
        {cell('Cost today', usd(d.spend.today), '#FF6961')}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,paddingTop:12,borderTop:'1px solid rgba(255,255,255,0.05)'}}>
        {cell('Tokens 30d (in/out)', `${k(d.tokens.month.in)} / ${k(d.tokens.month.out)}`)}
        {cell('Calls 30d', d.calls.month)}
        {cell('AI cost / yr', usd(d.spend.year), '#FF6961')}
      </div>
    </div>
  );
}

function UserModal({ user, onClose, onTierChange, onSuspend, onUnsuspend, onFlag, loading, fmtDate }) {
  const [newTier, setNewTier] = useState(user.subscriptionTier || 'free');
  const isGoldTier = user.subscriptionTier === 'scholar' || user.subscriptionTier === 'premium';

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',
      display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,
      backdropFilter:'blur(6px)'}}>
      <style>{`
        @keyframes modalIn{from{opacity:0;transform:scale(0.95) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}
        .user-modal{animation:modalIn 0.25s ease}
      `}</style>
      <div className="user-modal" style={{background:'#1A2332',
        border:'1px solid rgba(0,135,81,0.2)',
        borderRadius:20,width:580,maxHeight:'90vh',overflow:'auto',padding:32,
        boxShadow:'0 24px 80px rgba(0,0,0,0.5)'}}>

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:28}}>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <div style={{width:56,height:56,borderRadius:'50%',
              background:'linear-gradient(135deg, rgba(0,135,81,0.2), rgba(212,175,55,0.1))',
              display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,
              fontWeight:800,color: isGoldTier ? GOLD : ACCENT,
              border:`2px solid ${isGoldTier ? 'rgba(212,175,55,0.3)' : 'rgba(0,135,81,0.3)'}`}}>
              {(user.displayName||'?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{fontFamily:'Syne',fontWeight:800,fontSize:18}}>
                {user.displayName || 'Unknown'}
              </div>
              <div style={{fontSize:13,color:'rgba(255,255,255,0.4)',marginTop:2}}>{user.email}</div>
              <div style={{marginTop:6}}>
                <Badge label={user.subscriptionTier||'free'} color={user.subscriptionTier||'free'} />
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{background:'rgba(255,255,255,0.05)',border:'none',
            color:'rgba(255,255,255,0.4)',fontSize:18,cursor:'pointer',width:36,height:36,
            borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',
            transition:'all 0.2s'}}>✕</button>
        </div>

        {/* Usage Stats Bar */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:24}}>
          {[
            {label:'Questions Asked', value: user.totalQuestionsAsked || 0, color: ACCENT},
            {label:'Streak', value: `${user.streak || 0} days`, color: GOLD},
            {label:'Status', value: user.suspended ? 'Suspended' : 'Active',
              color: user.suspended ? '#ef4444' : ACCENT},
          ].map(s => (
            <div key={s.label} style={{padding:'14px',background:'#0F1419',
              borderRadius:12,textAlign:'center',border:'1px solid rgba(255,255,255,0.04)'}}>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.3)',
                textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>{s.label}</div>
              <div style={{fontSize:18,fontWeight:800,fontFamily:'Syne',color:s.color}}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Economics — what this user costs us vs what they pay */}
        <UserEconomics uid={user.id} />

        {/* Info Grid */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:24}}>
          {[
            ['UID',         user.id],
            ['Platform',    PLATFORM_META[platformOf(user)].label],
            ['Phone',       user.phone || '—'],
            ['Country',     user.country || '—'],
            ['Age Group',   user.ageGroup || '—'],
            ['Profession',  user.profession || '—'],
            ['Language',    user.language || '—'],
            ['Joined',      fmtDate(user.createdAt)],
            ['Last Active', fmtDate(user.lastActive)],
          ].map(([label,val]) => (
            <div key={label} style={{padding:'10px 14px',background:'#0F1419',
              borderRadius:10,border:'1px solid rgba(255,255,255,0.04)'}}>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.3)',
                textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>{label}</div>
              <div style={{fontSize:13,color:'rgba(255,255,255,0.8)',
                fontFamily:'DM Mono',wordBreak:'break-all'}}>{String(val)}</div>
            </div>
          ))}
        </div>

        {/* Tier Management */}
        <div style={{padding:16,background:'rgba(0,135,81,0.05)',
          border:'1px solid rgba(0,135,81,0.15)',borderRadius:12,marginBottom:20}}>
          <div style={{fontSize:11,color:'rgba(255,255,255,0.4)',
            marginBottom:10,textTransform:'uppercase',letterSpacing:1.5,fontWeight:600}}>
            Change Subscription Tier
          </div>
          <div style={{display:'flex',gap:10}}>
            <select value={newTier} onChange={e => setNewTier(e.target.value)}
              style={{flex:1,padding:'10px 12px',background:'#0F1419',
                border:'1px solid rgba(255,255,255,0.1)',borderRadius:10,
                color:'#fff',fontSize:13,outline:'none'}}>
              {['free','student','premium','scholar','family'].map(t => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>
              ))}
            </select>
            <GoldBtn onClick={() => onTierChange(user.id, newTier)} small disabled={loading}>
              {loading ? '...' : 'Apply'}
            </GoldBtn>
          </div>
        </div>

        {/* Actions */}
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <a href={`mailto:${user.email}`}
            style={{flex:1,padding:'10px',background:'rgba(59,130,246,0.08)',
              border:'1px solid rgba(59,130,246,0.15)',borderRadius:10,
              color:'#3b82f6',fontSize:13,fontWeight:600,
              textAlign:'center',textDecoration:'none',transition:'all 0.2s'}}>
            Email User
          </a>
          <GoldBtn onClick={() => onFlag(user.id)} small danger>
            Flag
          </GoldBtn>
          {user.suspended ? (
            <GoldBtn onClick={() => onUnsuspend(user.id)} small>
              Reactivate
            </GoldBtn>
          ) : (
            <GoldBtn onClick={() => onSuspend(user.id)} small danger>
              Suspend
            </GoldBtn>
          )}
          {/* Permanent ban — the ONLY path to a ban (auto-enforcement only
              warns/suspends). Drives accountStatus, enforced by the chat gate. */}
          {user.accountStatus === 'banned' ? (
            <GoldBtn small onClick={async () => {
              if (!window.confirm('Lift the permanent ban and reactivate this account?')) return;
              try {
                const fn = httpsCallable(getFunctions(), 'adminSetUserAccountStatus');
                await fn({ uid: user.id, status: 'active' });
                alert('Account reinstated.');
              } catch (e) { alert('Failed: ' + (e?.message || e)); }
            }}>
              Unban
            </GoldBtn>
          ) : (
            <GoldBtn small danger onClick={async () => {
              const reason = window.prompt('Reason for PERMANENT ban (recorded in the audit log):');
              if (reason === null) return;
              if (!window.confirm('Permanently BAN this account? They will be blocked from all chat.')) return;
              try {
                const fn = httpsCallable(getFunctions(), 'adminSetUserAccountStatus');
                await fn({ uid: user.id, status: 'banned', reason });
                alert('Account permanently banned.');
              } catch (e) { alert('Failed: ' + (e?.message || e)); }
            }}>
              Ban (permanent)
            </GoldBtn>
          )}
          {/* Warrant-gated, AUDITED export of this user's full chat history.
              Read-only (no impersonation). The backend logs who/when/why to
              admin_access_audit before returning any data. */}
          <GoldBtn small onClick={async () => {
            const reason = window.prompt(
              'Warrant reference / legal justification for this export.\n' +
              'This is logged for audit. Continue?');
            if (reason === null) return;
            try {
              const fn = httpsCallable(getFunctions(), 'adminExportUserData');
              const res = await fn({ uid: user.id, reason });
              // Human-readable transcript (court-ready) — opens in any browser.
              downloadBlob(buildReadableExportHtml(res.data), 'text/html',
                `oraku_user_${user.id}_readable.html`);
              // Raw JSON alongside, for completeness / machine processing.
              downloadBlob(JSON.stringify(res.data, null, 2), 'application/json',
                `oraku_user_${user.id}_export.json`);
            } catch (e) {
              alert('Export failed: ' + (e?.message || e));
            }
          }}>
            Download Conversations (Warrant)
          </GoldBtn>
        </div>
      </div>
    </div>
  );
}
